import type React from "react";
import { useEffect } from "react";
import classNames from "classnames";

import { isInvestorLoyaltyLevel } from "@/api/interfaces/loyaltyProgramsLayout";
import InvestorClubCardBanner from "@/base/blocks/MyInfoBlock/InvestorClubBanner";
import { Loader } from "@/base/components/Global";
import MyCard from "@/pages/HomePage/components/MyApolloClubCard/MyCard";
import {
  fetchInvestorLoyaltyBenefit,
  selectInvestorLoyaltyBenefit,
} from "@/store/cms";
import { useAppDispatch, useAppSelector } from "@/store/hooks";
import { fetchLoyaltyPrograms, selectLoyaltyPrograms } from "@/store/user";
import { hasData, shouldLoad } from "@/store/utils";
import { createUseStyles } from "@/theme";

const useStyles = createUseStyles(({ borderRadius, card }) => ({
  container: {
    width: "100%",
  },
  loadingContainer: {
    backgroundColor: "#0D0D0C",
    borderRadius: borderRadius.l,
    aspectRatio: card.ratio,
  },
}));

type MyInfoCardSectionProps = {
  className?: string;
};

const MyInfoCardSection: React.FC<MyInfoCardSectionProps> = ({ className }) => {
  const classes = useStyles();
  const dispatch = useAppDispatch();
  const loyaltyPrograms = useAppSelector(selectLoyaltyPrograms);
  const investorBenefit = useAppSelector(selectInvestorLoyaltyBenefit);

  useEffect(() => {
    if (shouldLoad(loyaltyPrograms)) {
      void dispatch(fetchLoyaltyPrograms({}));
    }
  }, [dispatch, loyaltyPrograms]);

  const isInvestor =
    hasData(loyaltyPrograms) &&
    loyaltyPrograms.array.some((program) =>
      isInvestorLoyaltyLevel(program.externalIdentifier),
    );

  useEffect(() => {
    if (isInvestor && shouldLoad(investorBenefit)) {
      void dispatch(fetchInvestorLoyaltyBenefit());
    }
  }, [dispatch, investorBenefit, isInvestor]);

  const hasLoyaltyProgramsLoaded =
    hasData(loyaltyPrograms) || loyaltyPrograms.status === "failed";
  const hasInvestorBenefitLoaded =
    hasData(investorBenefit) || investorBenefit.status === "failed";
  const shouldWaitForInvestorBenefit = isInvestor && !hasInvestorBenefitLoaded;
  const shouldWaitForSection =
    !hasLoyaltyProgramsLoaded || shouldWaitForInvestorBenefit;

  if (shouldWaitForSection) {
    return (
      <div className={classNames(classes.container, className)}>
        <div className={classes.loadingContainer}>
          <Loader showLoader isMinified />
        </div>
      </div>
    );
  }

  return (
    <div className={classNames(classes.container, className)}>
      <MyCard />
      {isInvestor && <InvestorClubCardBanner />}
    </div>
  );
};

export default MyInfoCardSection;
